import { gql } from '@apollo/client';
import IconExplorer from '@apollo/icons/default/IconExplorer.svg';
import { Heading, IconButton, Text } from '@apollo/orbit';
import { Telescope } from '@apollo/space-kit/illustrations/Telescope';
import React from 'react';
import { Link } from 'react-router-dom';

import { useInternalGraphLinking } from 'src/app/graph/hooks/useInternalGraphLinking';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { OrderStatus } from 'src/lib/graphqlTypes/types';

import { RouterOrderStatus } from '../../../launchHistoryList/launchHistoryListItem/LaunchHistoryListItem';
import {
  LaunchSchemaChangeSummaryFragment,
  SchemaChangeSummaryTable,
} from '../SchemaChangeSummaryTable/SchemaChangeSummaryTable';
import { SchemaHash } from '../SchemaHash';

import {
  ChangelogResult,
  launchChangelogResultFragment,
} from './ChangelogResult';
interface Props {
  launch: GraphQLTypes.LaunchDetailsCardFragment;
  isCloudSupergraph: Boolean;
}

export const LaunchDetailsCardFragment = gql`
  fragment LaunchDetailsCardFragment on Launch {
    id
    isCompleted
    schemaTag {
      id
      schema {
        createdAt
        hash
      }
      diffToPrevious {
        severity
        numberOfAffectedOperations
        affectedQueries {
          id
          name
          markedAsIgnored
          markedAsSafe
          changes {
            semanticChange {
              definition {
                code
                category
              }
              parentNode {
                name
                kind
              }
              childNode {
                name
                printedType
              }
              argNode {
                name
              }
            }
            impact
          }
        }
        changeSummary {
          ...LaunchSchemaChangeSummaryFragment
        }
      }
    }
    status
    order @skip(if: $skipOrder) {
      __typename
      ... on Order {
        id
        status
        reason
        router {
          id
          routerVersion {
            core
          }
        }
      }
    }
    build {
      input {
        __typename
      }
      result {
        # see if the current build has failed
        ... on BuildFailure {
          errorMessages {
            message
            code
            locations {
              column
              line
            }
          }
        }
      }
    }
    results {
      ... on ChangelogLaunchResult {
        schemaTagID
      }
      __typename
    }
    upstreamLaunch {
      id
      ...LaunchChangelogResultFragment
    }
    downstreamLaunches {
      id
      ...LaunchChangelogResultFragment
    }
    ...LaunchChangelogResultFragment
  }
  ${launchChangelogResultFragment}
  ${LaunchSchemaChangeSummaryFragment}
`;

export const LaunchDetailsCard = ({ launch, isCloudSupergraph }: Props) => {
  const { locationToExplorer } = useInternalGraphLinking();

  const { schemaTag, order } = launch;

  const buildFailed = launch.build?.result?.__typename === 'BuildFailure';

  const sortedDownstreamLaunches = [...launch.downstreamLaunches].sort((a, b) =>
    a.graphVariant.localeCompare(b.graphVariant),
  );

  const showRouterOrderDetails =
    isCloudSupergraph && order?.__typename === 'Order';

  return (
    <div className="ml-auto max-w-80 rounded border border-primary">
      <Heading as="h4" className="border-b border-primary py-2 pl-4">
        Details
      </Heading>

      <div className="p-4">
        {launch.results.length === 0 && (
          <div className="flex h-full flex-col items-center justify-center">
            <Telescope className="mb-5 flex size-full max-w-sm px-5" />
            <span className="text-sm text-neutral">
              {buildFailed
                ? 'No results found. Build failed.'
                : launch.status === GraphQLTypes.LaunchStatus.LAUNCH_INITIATED
                  ? 'No results yet.'
                  : 'No results.'}
            </span>
          </div>
        )}

        {launch.results.length > 0 && (
          <div className="space-y-4">
            {showRouterOrderDetails && (
              <div className="flex flex-col">
                <Text as="div" variant="label" className="mb-2">
                  Cloud Router
                </Text>
                <div className="flex items-center">
                  <RouterOrderStatus
                    status={order.status}
                    reason={order.reason && order.reason}
                  />
                  {order.status === OrderStatus.COMPLETED && (
                    <Tooltip label="Open in Explorer" placement="top">
                      <IconButton
                        aria-label="Open in Explorer"
                        variant="hidden"
                        size="sm"
                        icon={<IconExplorer />}
                        as={Link}
                        to={locationToExplorer({})}
                      />
                    </Tooltip>
                  )}
                  {order.router?.routerVersion?.core && (
                    <span className="ml-3 text-sm text-secondary">
                      v{order.router.routerVersion.core}
                    </span>
                  )}
                </div>
              </div>
            )}

            {launch.schemaTag?.schema.hash && (
              <div className="flex flex-col items-start">
                <Text as="div" variant="label" className="mb-2">
                  Schema hash
                </Text>
                <SchemaHash hash={launch.schemaTag?.schema.hash} />
              </div>
            )}

            <div>
              <Text as="div" variant="label" className="mb-2">
                Types and fields delta
              </Text>
              <SchemaChangeSummaryTable
                changes={schemaTag?.diffToPrevious?.changeSummary}
              />
            </div>

            <div>
              <Text as="div" variant="label" className="mb-2">
                Changelog {launch.downstreamLaunches.length > 0 && 'entries'}
              </Text>

              <ChangelogResult launch={launch} showIcon={true} />
              {sortedDownstreamLaunches.length > 0 &&
                sortedDownstreamLaunches.map((derivedLaunch) => (
                  <div
                    key={derivedLaunch.id}
                    className="ml-4 border-l border-primary pl-2"
                  >
                    <ChangelogResult launch={derivedLaunch} />
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
